<template>
  <div class="comment_container" :class="{ isRepost: postType === 'repost' }">
    <div class="list">
      <CommentItemComponent
        v-for="item in comments"
        :key="item._id"
        :comment="item"
        :sourceId="item._id"
        :sourceType="sourceType"
        :postId="sourceId"
        :all-comments="comments"
      />
    </div>
    <div v-if="hasMoreComments" class="load-more-comments">
      <button
        type="button"
        class="load-more-btn"
        :disabled="isLoadingMoreComments"
        @click="loadMoreComments"
      >
        {{ isLoadingMoreComments ? "Loading..." : "Load More" }}
      </button>
    </div>
    <Teleport v-if="postType === 'repost'" to=".postDetailsWrapper" defer>
      <CommentTextfieldComponent
        :class="{ isRepost: postType === 'repost' }"
        :sourceId="sourceId"
        :sourceType="sourceType"
        :postId="sourceId"
      />
    </Teleport>
    <CommentTextfieldComponent
      v-else
      :sourceId="sourceId"
      :sourceType="sourceType"
      :postId="sourceId"
    />
  </div>
</template>

<script lang="ts">
import utilService from "@/services/util.service";
import { defineComponent, onMounted, onUnmounted, ref } from "vue";
import CommentTextfieldComponent from "./CommentTextfieldComponent.vue";
import CommentItemComponent from "./CommentItemComponent.vue";
import { useComment } from "../../composables/comments.composable";

export default defineComponent({
  props: ["sourceId", "sourceType", "postType"],
  components: {
    CommentItemComponent,
    CommentTextfieldComponent,
  },
  setup(props) {
    const {
      comments,
      getComments,
      clearComments,
      hasMoreComments,
      loadMoreComments,
      isLoadingMoreComments,
    } = useComment();
    const sourceId = ref(props.sourceId);
    const sourceType = ref(props.sourceType);

    const getImageUrl = (url: string, directory: string | null) =>
      utilService.getImageUrl(url, directory);

    onMounted(() => {
      if (sourceId.value) {
        getComments(sourceId.value);
      }
    });

    onUnmounted(() => {
      clearComments();
    });

    return {
      getImageUrl,
      sourceId,
      sourceType,
      comments,
      hasMoreComments,
      loadMoreComments,
      isLoadingMoreComments,
    };
  },
});
</script>

<style lang="scss" scoped>
.comment_container {
  background-color: var(--base-regular) !important;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &.isRepost {
    min-height: auto !important;
  }

  .list {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 8px 0 4px;
    @include scrollbar();
  }

  .load-more-comments {
    flex-shrink: 0;
    padding: 8px 0;
    border-top: 1px solid var(--base-new-border);
  }

  .load-more-btn {
    background: transparent;
    color: var(--text-color);
    border-radius: 8px;
    height: 40px;
    border: none;
    width: auto;
    cursor: pointer;
    font-size: var(--font-sm);
    font-weight: 500;
    transition: opacity 0.2s ease-in-out;
    margin: 0px auto;
    display: flex;
    align-items: center;
    justify-content: center;
    text-decoration: underline;

    &:hover:not(:disabled) {
      opacity: 0.85;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  :deep(.comment_footer) {
    flex-shrink: 0;
  }
}
</style>
